import { Song } from '../types/song';

export interface MusicPlatform {
  id: string;
  name: string;
  urlKey: keyof Pick<
    Song,
    | 'spotifyUrl'
    | 'youtubeMusicUrl'
    | 'appleMusicUrl'
    | 'amazonMusicUrl'
    | 'jioSaavnUrl'
    | 'gaanaUrl'
  >;
  brand: string; // Tailwind gradient fragment, e.g. "from-[#1DB954] to-[#159c43]"
}

// Single source of truth: this order drives both the "Listen Everywhere" card
// order and the Hero CTA fallback priority (first platform with a URL wins).
// Reflects streaming popularity for the target (India) audience.
export const MUSIC_PLATFORMS: MusicPlatform[] = [
  { id: 'youtubeMusic', name: 'YouTube Music', urlKey: 'youtubeMusicUrl', brand: 'from-[#FF0000] to-[#cc0000]' },
  { id: 'spotify', name: 'Spotify', urlKey: 'spotifyUrl', brand: 'from-[#1DB954] to-[#159c43]' },
  { id: 'amazonMusic', name: 'Amazon Music', urlKey: 'amazonMusicUrl', brand: 'from-[#25D1DA] to-[#1a9ba2]' },
  { id: 'jioSaavn', name: 'JioSaavn', urlKey: 'jioSaavnUrl', brand: 'from-[#2BC5B4] to-[#1e8f83]' },
  { id: 'appleMusic', name: 'Apple Music', urlKey: 'appleMusicUrl', brand: 'from-[#FA2D48] to-[#c41f36]' },
  { id: 'gaana', name: 'Gaana', urlKey: 'gaanaUrl', brand: 'from-[#E72C30] to-[#b52125]' },
];

export function getAvailablePlatforms(song: Song): { platform: MusicPlatform; url: string }[] {
  return MUSIC_PLATFORMS
    .map((platform) => ({ platform, url: song[platform.urlKey] }))
    .filter((entry): entry is { platform: MusicPlatform; url: string } => !!entry.url);
}

export function getPrimaryPlatform(song: Song): { platform: MusicPlatform; url: string } | null {
  const available = getAvailablePlatforms(song);
  return available.length > 0 ? available[0] : null;
}

// The Hero CTA target: a manual heroCtaUrl override always wins; otherwise it
// falls back to the highest-priority platform link above. This is the one
// place that decides the CTA — nothing else should hardcode platform priority.
export function getHeroCtaUrl(song: Song): string | null {
  const manual = song.heroCtaUrl?.trim();
  if (manual) return manual;
  return getPrimaryPlatform(song)?.url ?? null;
}
